import React, { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import FieldPageChrome from "../../components/field/FieldPageChrome";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import RentalAssetChecklist from "../../components/rentals/RentalAssetChecklist";
import { DASH, GradeBadge, TypeChip, TYPE_LABEL, type InspectionType } from "../../components/rentals/InspectionBits";
import { inspectionPhotoExtras, rentalChecklistComplete, rentalOverall, structuredInspectionBody, type RentalAnswers } from "../../lib/rental-inspections";
import { RentalInspectionPhotoField, newInspectionId } from "../../components/rentals/RentalInspectionPhotos";
import type { RentalChecklistResponse } from "../../types/rental-inspection";
import { DhPanel } from "../downhole/DownholeBits";

/**
 * A new rental inspection, /rentals/inspections/new (#815 Inspections). The
 * register's New Inspection dialog picks the asset, the order and the type and
 * comes here with them; nothing exists on the server until Submit, which files
 * it (POST /inspections, the structured checklist of api#861) and opens its
 * page. Reached any other way — a reload, a typed URL — there is no draft, and
 * the page says to start one from Inspections.
 */

export interface InspectionDraft {
  asset_id: string;
  asset_name: string;
  asset_tag: string;
  order_id: string;
  order_number: string;
  customer_name: string;
  type: InspectionType;
  /**
   * Set when the inspection is started from a dispatch's page (api#1068):
   * sent as `dispatch_id`, which the API checks belongs to this tenant and
   * carries this asset on its order (else 404, shown as sent). Absent from the
   * register and the yard, which inspect with no dispatch.
   */
  dispatch_id?: string;
  dispatch_label?: string;
}

/**
 * `surface="field"`: the rental tech's New Inspection, reached from the field
 * Inspections tile (fe#815). The casing field header and back arrow in place of
 * the office band; the checklist and the submit are the same code.
 */
export default function RentalInspectionDraft({ surface = "office" }: { surface?: "office" | "field" } = {}) {
  const isField = surface === "field";
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const draft = (location.state as { draft?: InspectionDraft } | null)?.draft ?? null;

  /** The inspector's real name; `inspector_id` comes from the JWT server-side. */
  const inspectorName = user ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "" : "";

  const [answers, setAnswers] = useState<RentalAnswers>({});
  const [checklist, setChecklist] = useState<RentalChecklistResponse | undefined>(undefined);
  const onChecklist = useCallback((cl: RentalChecklistResponse) => setChecklist(cl), []);
  const [submitting, setSubmitting] = useState(false);
  /** Minted here so photos can be uploaded against it before Submit files it (api#1066). */
  const [inspectionId] = useState(newInspectionId);
  const [photos, setPhotos] = useState<string[]>([]);
  const [photosBusy, setPhotosBusy] = useState(false);

  const overall = rentalOverall(answers);
  const complete = rentalChecklistComplete(checklist, answers);

  const handleSubmit = async () => {
    if (!draft || !checklist) return;
    setSubmitting(true);
    try {
      const res = await api.rentals.inspections.create(
        structuredInspectionBody(draft.asset_id, draft.type, checklist, answers, {
          order_id: draft.order_id || undefined,
          dispatch_id: draft.dispatch_id || undefined,
          inspector_name: inspectorName || undefined,
          ...inspectionPhotoExtras(inspectionId, photos),
        }),
      );
      const data = (res.data as { data?: { id?: string; maintenance?: { work_order_number?: string } | null } })?.data;
      await queryClient.invalidateQueries({ queryKey: ["rentals", "inspections"] });
      toast.success(
        data?.maintenance?.work_order_number
          ? `Inspection submitted. ${draft.asset_name} is in maintenance; work order ${data.maintenance.work_order_number} is open.`
          : "Inspection submitted.",
      );
      // The filed inspection's own page; a reload of /new must not file it again.
      navigate(data?.id ? `/rentals/inspections/${encodeURIComponent(data.id)}` : "/rentals/inspections", { replace: true });
    } catch (err) {
      // Every answer stays on screen, and the draft is still a draft.
      toast.error(getApiErrorMessage(err, "Failed to submit inspection"));
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <>
          {!draft ? (
            <p data-testid="inspection-no-draft" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center text-sm text-fieldiq-text">
              No inspection is being filled in. Start one from{" "}
              <Link className="underline underline-offset-2" to="/rentals/inspections">Inspections</Link>.
            </p>
          ) : (
            <DhPanel eyebrow="Checklist" title="Inspection" testId="inspection-draft">
              <div className="space-y-6 p-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Asset</h3>
                    <p className="text-sm font-bold text-fieldiq-text">{draft.asset_name}</p>
                    <p className="text-xs text-fieldiq-muted">Asset no. {draft.asset_tag}</p>
                  </div>
                  <div className="space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Order</h3>
                    <p className="text-sm font-bold text-fieldiq-text">{draft.order_number}</p>
                    <p className="text-xs text-fieldiq-muted">{draft.customer_name}</p>
                    {draft.dispatch_id && (
                      <p className="text-xs text-fieldiq-muted" data-testid="inspection-draft-dispatch">
                        On dispatch{" "}
                        {/* The dispatch page is an office route; the field tree names it only. */}
                        {isField ? (
                          draft.dispatch_label || "this dispatch"
                        ) : (
                          <Link className="underline underline-offset-2" to={`/rentals/dispatches/${encodeURIComponent(draft.dispatch_id)}`}>
                            {draft.dispatch_label || "Open dispatch"}
                          </Link>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <TypeChip type={draft.type} />
                  <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">
                    Unsaved draft
                  </span>
                  <span className="text-sm text-fieldiq-muted">
                    Performed by <span className="text-fieldiq-text">{inspectorName || DASH}</span>
                  </span>
                  <GradeBadge grade={overall} prefix="Overall: " />
                </div>

                <RentalAssetChecklist assetId={draft.asset_id} inspectionType={draft.type} answers={answers} onChange={setAnswers} onChecklist={onChecklist} />

                <RentalInspectionPhotoField inspectionId={inspectionId} idPrefix="inspection-draft" onChange={setPhotos} onBusyChange={setPhotosBusy} />

                {/* What submitting does, and nothing it does not. */}
                {overall === "fail" && (
                  <div role="note" data-testid="inspection-fail-note" className="flex items-center gap-3 rounded-fi-control border border-fieldiq-border bg-fi-red/10 p-4">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
                    <p className="text-sm text-fieldiq-text">
                      A Fail puts <strong>{draft.asset_name}</strong> in maintenance and opens a work order. Approving the work order returns it to Available.
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => navigate("/rentals/inspections")} className="fi-action fi-action--standard border border-fieldiq-border bg-fieldiq-card text-fieldiq-text">
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !complete || !overall || photosBusy}
                    className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
                  >
                    {submitting ? (<><Loader2 className="size-4 animate-spin" aria-hidden="true" />Submitting...</>) : "Submit Inspection"}
                  </button>
                </div>
                {!complete && <p className="text-right text-xs text-fieldiq-muted">Answer every item to submit.</p>}
                {complete && !overall && <p className="text-right text-xs text-fieldiq-muted">Every item is N/A: nothing was inspected.</p>}
              </div>
            </DhPanel>
          )}
    </>
  );

  if (isField) {
    return (
      <FieldPageChrome testId="inspection-draft-page" title={draft ? draft.asset_name : "New inspection"} onBack={() => navigate("/rentals/inspections")}>
        {body}
      </FieldPageChrome>
    );
  }

  return (
    <div data-testid="inspection-draft-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={draft ? `New inspection · ${TYPE_LABEL[draft.type]}` : "New inspection"}
        title={draft ? draft.asset_name : "New inspection"}
        onBack={() => navigate("/rentals/inspections")}
        backLabel="Back to Inspections"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {body}
        </div>
      </section>
    </div>
  );
}
